import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { formatCurrency } from '../utils/helpers';
import { buildCalculationRequest, toCanonicalMarkupType } from '../utils/quotationUtils';
import { buildCalcSnapshot, diffCalcSnapshots } from '../utils/calcSignature';
import { Loader2, FileText, Package } from 'lucide-react';
import CustomPackageHotelSelector from '../components/CustomPackageHotelSelector';

// Import refactored components
import {
  StepIndicator,
  GuestTripStep,
  PreferencesStep,
  NavigationButtons,
  TermsSection,
  TripSummaryAccordion,
  useQuotationForm,
} from '../components/QuotationWizard';

const CreateQuotation = () => {
  const navigate = useNavigate();
  const { buildPath } = useTenant();
  const { user, isB2BAgent } = useAuth();
  
  // Track if changes have been made since last calculation (for Step 4 warning)
  const [hasChanges, setHasChanges] = useState(false);
  // Stale-calc guard: labelled snapshot of the price-affecting inputs that
  // produced `calculationResult`. Compared against the live form every render.
  const [calcSnapshot, setCalcSnapshot] = useState(null);
  
  // Ref to track current step (avoids stale closure in async functions)
  const stepRef = useRef(1);
  
  // Use the custom hook for form state
  const {
    step,
    loading,
    calculating,
    routes,
    places,
    travelAgents,
    calculationResult,
    availableMealPlans,
    availableCategories,
    vehicleOptions,
    selectedVehicles,
    customPackageHotelSelections,
    formData,
    classificationMode,
    setStep,
    setLoading,
    setCalculating,
    setCalculationResult,
    setCustomPackageHotelSelections,
    setSelectedVehicles,
    setFormData,
    handleInputChange,
    toggleMealPlan: originalToggleMealPlan,
    toggleCategory: originalToggleCategory,
    addCustomStop,
    removeCustomStop,
    updateCustomStop,
    nextStep,
    prevStep,
    goToStep,
    invalidateCalculation,
    switchClassificationMode,
    refreshPlaces,
    totalPax,
    suggestedSingleRooms,
    suggestedDoubleRooms,
  } = useQuotationForm();

  // Keep stepRef in sync with step state
  useEffect(() => {
    stepRef.current = step;
  }, [step]);

  // ── Stale-calc guard ────────────────────────────────────────────────────
  // Any edit to a price-affecting input (pax, dates, route/stops, hotels,
  // categories, meal plans, vehicle, rooms, markup, discount, GST, misc costs,
  // P2P) invalidates the numbers on screen AND the calc blob the Custom /
  // Manual / DMC save endpoints trust. Detect it by fingerprint so it works
  // no matter which step the edit happened on.
  const liveCalcSnapshot = useMemo(
    () => buildCalcSnapshot(formData, { customPackageHotelSelections, selectedVehicles }),
    [formData, customPackageHotelSelections, selectedVehicles]
  );
  const staleChanges = useMemo(
    () => (calculationResult && calcSnapshot ? diffCalcSnapshots(calcSnapshot, liveCalcSnapshot) : []),
    [calculationResult, calcSnapshot, liveCalcSnapshot]
  );
  const isStale = staleChanges.length > 0 || hasChanges;

  // Custom Package: the server picks the base vehicle for the party on the
  // first calculate (cheapest overall, same rule as Predefined).

  // Wrap toggleMealPlan to track changes
  const toggleMealPlan = useCallback((plan) => {
    originalToggleMealPlan(plan);
    if (calculationResult && step === 3) setHasChanges(true);
  }, [originalToggleMealPlan, calculationResult, step]);

  // Wrap toggleCategory to track changes
  const toggleCategory = useCallback((category) => {
    originalToggleCategory(category);
    if (calculationResult && step === 3) setHasChanges(true);
  }, [originalToggleCategory, calculationResult, step]);

  // Handle vehicle selection changes
  const handleVehicleSelectionChange = (vehicleType, isSelected) => {
    if (isSelected) {
      setSelectedVehicles(prev => [...prev, vehicleType]);
    } else {
      setSelectedVehicles(prev => prev.filter(v => v !== vehicleType));
    }
    // Custom package ticks only pick which upgrades are offered; price is unchanged.
    if (calculationResult && step === 3 && formData.route_type !== 'custom_package') setHasChanges(true);
  };

  // Build stops with hotels for ONE option's selection map.
  // `selections` defaults to the legacy single-option state; pass an option's
  // `selected_hotels` to build that option's stops. Stop dates are
  // option-independent, so they always fall back to the shared map.
  const buildStopsWithHotels = (selections) => {
    const pkg = formData.custom_package_data;
    const source = selections || customPackageHotelSelections || {};
    return (pkg?.stops || []).map((stop, index) => {
      const key = `stop_${index}`;
      const shared = customPackageHotelSelections[key] || {};
      const optionSel = source[key] || {};
      const selection = {
        check_in: optionSel.check_in || shared.check_in,
        check_out: optionSel.check_out || shared.check_out,
        hotels: Array.isArray(optionSel.hotels)
          ? optionSel.hotels
          : (optionSel.hotel_id ? [optionSel] : []),
      };
      
      // Handle new multi-hotel structure with room_name field
      // Include date-aware pricing info (lowest_rate, check_in, check_out)
      const hotelsArray = Array.isArray(selection.hotels) 
        ? selection.hotels.map(h => ({
            hotel_id: h.hotel_id,
            hotel_name: h.hotel_name,
            room_name: h.room_name || h.room_type || '', // Support both room_name and legacy room_type
            room_type: h.room_name || h.room_type || '', // Keep room_type for backend compatibility
            star_rating: h.star_rating || null,
            meal_plan: h.meal_plan || 'CP',
            nights: h.nights || 1,
            lowest_rate: h.lowest_rate || 0, // Date-aware rate from frontend
            room_gst_slab: h.room_gst_slab ?? null, // from master; backend re-resolves
            check_in: h.check_in || selection.check_in, // Check-in date
            check_out: h.check_out || selection.check_out, // Check-out date
          }))
        : [{
            hotel_id: selection.hotel_id || '',
            hotel_name: selection.hotel_name || '',
            room_name: selection.room_name || selection.room_type || '',
            room_type: selection.room_name || selection.room_type || '',
            star_rating: selection.star_rating || null,
            meal_plan: selection.meal_plan || 'CP',
            nights: selection.nights || stop.nights || 1,
            lowest_rate: selection.lowest_rate || 0,
            room_gst_slab: selection.room_gst_slab ?? null,
            check_in: selection.check_in,
            check_out: selection.check_out,
          }];
      
      return {
        place_id: stop.place_id,
        place_name: stop.place_name,
        // P1-B fix (2026-04-30): Backend `StopSchema.order` is required int.
        // Wizard stops carry `order_seq` (not `order`); fall back through both,
        // then array index+1 as last-resort. Prevents "Field required" 422 toast.
        order: stop.order ?? stop.order_seq ?? (index + 1),
        nights: stop.nights,
        check_in: selection.check_in, // Pass check-in date for the stop
        check_out: selection.check_out, // Pass check-out date for the stop
        hotels: hotelsArray
      };
    });
  };

  // One price tier per option (2026-09-27). Options with no hotels selected at
  // all are skipped so an untouched draft option never adds a ₹0 tier.
  const buildCustomOptions = () => {
    const raw = formData.custom_package_options || [];
    const built = raw.map((opt, idx) => ({
      option_id: String(opt.id ?? idx + 1),
      option_name: (opt.option_name || `Option ${idx + 1}`).trim() || `Option ${idx + 1}`,
      stops: buildStopsWithHotels(opt.selected_hotels || {}),
    }));
    return built.filter(o => o.stops.some(s => (s.hotels || []).length > 0));
  };

  // Debounce timer ref for calculation
  const calculateDebounceRef = useRef(null);
  // Week-3 (2026-06-28): tracks the AbortController for the in-flight
  // /calculate-* request. When a new calculate fires (e.g. user types fast,
  // 500ms debounce + this controller means at most one calc round-trip is
  // ever live), we abort the previous one. Without this, axios responses
  // can return out of order and the stale one's setCalculationResult would
  // overwrite the fresh one — exactly the kind of preview-vs-saved drift
  // Week-2 set out to eliminate.
  const calculateAbortRef = useRef(null);
  
  // Debounced calculate function - prevents rapid API calls
  const calculateQuotationDebounced = useCallback(() => {
    // Clear any pending calculation
    if (calculateDebounceRef.current) {
      clearTimeout(calculateDebounceRef.current);
    }
    
    // Set new debounced calculation (500ms delay)
    calculateDebounceRef.current = setTimeout(() => {
      calculateQuotationImmediate();
    }, 500);
  }, [formData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Actual calculate quotation (called after debounce).
  // Returns the fresh calculation result (or null on failure/abort) so callers
  // like saveQuotation can use it immediately instead of the stale React state.
  const calculateQuotationImmediate = async () => {
    // Cancel any in-flight calc — only the latest user input may win.
    if (calculateAbortRef.current) {
      calculateAbortRef.current.abort();
    }
    const controller = new AbortController();
    calculateAbortRef.current = controller;
    const reqConfig = { signal: controller.signal };

    // Fingerprint of the inputs this calculation is based on.
    const snapshotAtCalc = buildCalcSnapshot(formData, {
      customPackageHotelSelections,
      selectedVehicles,
    });
    const commitCalc = (result) => {
      setCalculationResult(result);
      setCalcSnapshot(snapshotAtCalc);
      setHasChanges(false);
      return result;
    };

    setCalculating(true);
    try {
      if (formData.route_type === 'dmc_package') {
        // For DMC Package - use dmc_content from parsed or manually entered data
        const dmcContent = formData.dmc_content;
        
        if (!dmcContent || !dmcContent.parsed || !dmcContent.parsed.package_title) {
          // No parsed data yet - show empty calculation result for manual entry
          const calculationResult = {
            is_dmc_package: true,
            dmc_content: dmcContent,
            category_pricing: {
              'DMC': {
                total_cost: 0,
                base_cost: 0,
                markup_amount: 0,
                discount_amount: 0,
                gst_amount: 0,
                per_person_cost: 0,
              }
            },
          };
          toast.info('Enter package details or use AI to parse DMC content');
          return commitCalc(calculationResult);
        } else {
          // Has parsed data - calculate with backend
          const { endpoint, payload } = buildCalculationRequest(formData);

          const response = await api.post(endpoint, payload, reqConfig);
          if (stepRef.current === 2) setStep(3);
          toast.success('DMC Package calculated!');
          return commitCalc({ ...response.data, is_dmc_package: true });
        }
      } else if (formData.route_type === 'manual_package') {
        // Manual Package — canonical payload via buildCalculationRequest
        const { endpoint, payload } = buildCalculationRequest(formData);
        const response = await api.post(endpoint, payload, reqConfig);
        if (stepRef.current === 2) setStep(3);
        toast.success('Manual Package calculated!');
        return commitCalc({ ...response.data, is_manual_package: true });
      } else if (formData.route_type === 'custom_package') {
        const customOptions = buildCustomOptions();
        const stopsWithHotels = buildStopsWithHotels();
        const { endpoint, payload } = buildCalculationRequest(formData, {
          stopsWithHotels,
          options: customOptions,
        });

        const response = await api.post(endpoint, payload, reqConfig);
        // Auto-advance to Step 3 (Preview) after Custom Package calculation
        if (stepRef.current === 2) setStep(3);
        toast.success('Custom package calculated!');
        return commitCalc({ ...response.data, is_custom_package: true });
      } else {
        // Predefined / Custom (route-based) — canonical payload via buildCalculationRequest
        const { endpoint, payload } = buildCalculationRequest(formData);

        const response = await api.post(endpoint, payload, reqConfig);
        // Auto-advance to Step 4 (Preview) after calculation
        if (stepRef.current === 2) {
          setStep(3);
        }
        toast.success('Quote calculated successfully!');
        return commitCalc(response.data);
      }
    } catch (error) {
      // Aborted-by-newer-request → silent no-op, do NOT overwrite the
      // pending calc state. axios sets error.name='CanceledError' (or 'AbortError').
      if (error.name === 'CanceledError' || error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        return null;
      }
      console.error('Calculation error:', error);
      // Handle Pydantic validation errors (array of error objects)
      const errorData = error.response?.data;
      let errorMessage = 'Failed to calculate quotation';
      
      if (errorData) {
        if (typeof errorData === 'string') {
          errorMessage = errorData;
        } else if (errorData.detail) {
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (Array.isArray(errorData.detail)) {
            // Pydantic validation errors
            errorMessage = errorData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else if (typeof errorData.detail === 'object') {
            errorMessage = errorData.detail.msg || errorData.detail.message || JSON.stringify(errorData.detail);
          }
        } else if (Array.isArray(errorData)) {
          errorMessage = errorData.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      
      toast.error(errorMessage);
      return null;
    } finally {
      // Only clear the calculating spinner if this controller is still the
      // active one — if a newer calc has already replaced us, leave its
      // spinner alone.
      if (calculateAbortRef.current === controller) {
        setCalculating(false);
        calculateAbortRef.current = null;
      }
    }
  };

  // Save quotation. `recalculate: true` (from the "Recalculate & Save" button)
  // refreshes prices first and saves the FRESH result.
  const saveQuotation = async ({ recalculate = false } = {}) => {
    if (!calculationResult) {
      toast.error('Please calculate the quotation first');
      return;
    }

    // Stale-calc guard — the Custom / Manual / DMC endpoints persist the calc
    // blob we send them, so saving a stale one either prices the trip wrong or
    // gets rejected by the canonical validator (I1/I2) and loses the quote.
    // React state updates are async, so we save the value calculate RETURNS
    // rather than re-reading `calculationResult`.
    let effectiveCalc = calculationResult;
    if (isStale) {
      if (!recalculate) {
        toast.error('Prices are out of date. Recalculate the quote before saving.');
        return;
      }
      const fresh = await calculateQuotationImmediate();
      if (!fresh) {
        toast.error('Could not refresh prices — nothing was saved.');
        return;
      }
      effectiveCalc = fresh;
    }

    setLoading(true);
    try {
      let response;
      
      if (formData.route_type === 'manual_package') {
        // For Manual Package, use the NEW structure from ManualPackageBuilder
        // formData.manual_package_data contains: route, options, p2p_transfers, itinerary
        const mpd = formData.manual_package_data || {};
        const route = mpd.route || [];
        const options = mpd.options || [];
        const p2pTransfers = mpd.p2p_transfers || [];
        const itinerary = mpd.itinerary || [];
        
        // Calculate totals from options
        const totalNights = mpd.total_nights || route.reduce((sum, s) => sum + (parseInt(s.nights) || 0), 0);
        const totalDays = mpd.total_days || totalNights + 1;
        
        // Get primary option for base pricing
        const primaryOption = options[0] || {};
        
        // Build arrival/departure from route
        const firstStop = route[0] || {};
        const lastStop = route[route.length - 1] || route[0] || {};
        
        console.log('Saving Manual Package - NEW structure:', {
          route: route,
          options: options,
          p2pTransfers: p2pTransfers,
          itinerary: itinerary,
          totalNights: totalNights,
          totalDays: totalDays,
        });
        
        // Build the manual_package_data in the correct format for backend
        const manualPackageData = {
          // NEW structure fields
          route: route,
          options: options,
          p2p_transfers: p2pTransfers,
          itinerary: itinerary,
          total_nights: totalNights,
          total_days: totalDays,
          // Legacy fields for compatibility
          arrival_city: formData.arrival_city || firstStop.place_name || '',
          arrival_date: formData.arrival_date,
          arrival_point: formData.arrival_point || 'Airport',
          departure_city: formData.departure_city || lastStop.place_name || '',
          departure_point: 'Airport',
        };
        
        // Use calculationResult which has the correct pricing
        const manualCalculation = effectiveCalc || {
          is_manual_package: true,
          total_nights: totalNights,
          total_days: totalDays,
        };
        
        const manualPayload = {
          manual_package_data: manualPackageData,
          manual_package_calculation: manualCalculation,
          guest_name: formData.guest_name,
          guest_phone: formData.guest_phone,
          guest_country_code: formData.guest_country_code || '+91',
          guest_email: formData.guest_email,
          arrival_city: manualPackageData.arrival_city,
          arrival_point: manualPackageData.arrival_point,
          arrival_date: formData.arrival_date,
          departure_city: manualPackageData.departure_city,
          tour_start_date: formData.arrival_date,
          total_nights: totalNights,
          total_days: totalDays,
          no_adults: formData.no_adults,
          extra_adults: 0,
          no_children_with_bed: formData.no_children_with_bed,
          no_children_no_bed: formData.no_children_no_bed,
          no_infants: formData.no_infants,
          markup_pct: formData.markup_pct || 0,
          markup_type: formData.markup_type || 'percent',
          discount_pct: formData.discount_type === 'percent' || !formData.discount_type ? (formData.discount_pct || 0) : 0,
          discount_type: formData.discount_type || 'percent',
          discount_amount: formData.discount_type === 'amount' ? (formData.discount_amount || 0) : 0,
          gst_percentage: formData.gst_percentage || 0,
          gst_included: formData.gst_included,
          gst_apply_on: formData.gst_apply_on || 'total',
          quotation_format: formData.quotation_format || 'total',
          show_breakup: formData.show_breakup,
          show_discount_separately: formData.show_discount_separately || false,
          show_misc_table: formData.show_misc_table !== false,
          misc_costs: formData.misc_costs || [],
          p2p_transfers: p2pTransfers.map(p => ({
            from_place: p.from_place,
            to_place: p.to_place,
            transport_mode: p.transport_mode,
            price_type: p.price_type,
            quantity: p.quantity,
            rate: parseFloat(p.cost) || 0,
            gst_slab: parseFloat(p.gst_pct) || 0,
          })),
          inclusions: formData.inclusions,
          exclusions: formData.exclusions,
          payment_terms: formData.payment_terms,
          cancellation_policy: formData.cancellation_policy,
          terms_conditions: formData.terms_conditions,
          room_config: {
            single_rooms: formData.single_rooms || 0,
            double_rooms: formData.double_rooms || 1,
            triple_rooms: formData.triple_rooms || 0,
          },
        };

        response = await api.post('/quotations/manual-package', manualPayload);
      } else if (formData.route_type === 'custom_package') {
        // For Custom Package, use the dedicated endpoint
        const pkg = formData.custom_package_data;
        const customOptions = buildCustomOptions();
        const stopsWithHotels = customOptions[0]?.stops || buildStopsWithHotels();
        // Vehicle + transfer days are SHARED and owned by Step 3.
        const resolvedVehicle =
          formData.vehicle_type_selected || formData.base_vehicle_type || pkg?.vehicle_type || '';
        const resolvedTransferDays =
          effectiveCalc?.transfer_days ||
          (formData.transfer_days_auto === false ? formData.transfer_days : null) ||
          (stopsWithHotels.reduce((sum, s) => sum + (s.nights || 0), 0) || 0) + 1;

        // Get extra_adults from room settings or calculation
        const extraAdults = formData.room_settings?.adult_extra_beds || effectiveCalc?.extra_adults || 0;
        
        const customPayload = {
          custom_package_data: {
            ...pkg,
            stops: stopsWithHotels,
            // Persist every option so the quote can be re-opened / cloned with
            // all tiers intact.
            options: customOptions,
            vehicle_type: resolvedVehicle,
            transfer_days: resolvedTransferDays,
          },
          custom_package_calculation: effectiveCalc,
          guest_name: formData.guest_name,
          guest_phone: formData.guest_phone,
          guest_country_code: formData.guest_country_code || '+91',
          guest_email: formData.guest_email,
          arrival_city: pkg?.arrival_city || formData.arrival_city,
          arrival_point: pkg?.arrival_point || formData.arrival_point,
          arrival_date: pkg?.arrival_date || formData.arrival_date,
          departure_city: pkg?.departure_city || formData.departure_city,
          tour_start_date: pkg?.arrival_date || formData.arrival_date,
          no_adults: formData.no_adults,
          extra_adults: extraAdults,
          no_children_with_bed: formData.no_children_with_bed,
          no_children_no_bed: formData.no_children_no_bed,
          no_infants: formData.no_infants,
          vehicle_type_selected: resolvedVehicle,
          vehicle_counts: formData.vehicle_counts || {},
          selected_vehicles: selectedVehicles || [],
          version_of: formData.version_of || null,
          transfer_days: resolvedTransferDays,
          markup_pct: formData.markup_type === 'amount' ? 0 : (formData.markup_pct || 0),
          markup_type: toCanonicalMarkupType(formData.markup_type, formData.markup_per_person),
          markup_amount: formData.markup_type === 'amount' ? (formData.markup_amount || 0) : 0,
          markup_per_person: formData.markup_per_person || false,
          discount_pct: formData.discount_type === 'percent' || !formData.discount_type ? (formData.discount_pct || 0) : 0,
          discount_type: formData.discount_type || 'percent',
          discount_amount: formData.discount_type === 'amount' ? (formData.discount_amount || 0) : 0,
          discount_per_person: formData.discount_per_person || false,
          p2p_transfers: formData.p2p_transfers || [],
          gst_percentage: formData.gst_percentage,
          gst_included: formData.gst_included,
          gst_apply_on: formData.gst_apply_on || 'total',
          quotation_format: formData.quotation_format || 'total',
          show_breakup: formData.show_breakup,
          show_discount_separately: formData.show_discount_separately || false,
          show_misc_table: formData.show_misc_table !== false,
          misc_costs: formData.misc_costs || [],
          inclusions: formData.inclusions,
          exclusions: formData.exclusions,
          payment_terms: formData.payment_terms,
          cancellation_policy: formData.cancellation_policy,
          terms_conditions: formData.terms_conditions,
        };

        response = await api.post('/quotations/custom-package', customPayload);
      } else if (formData.route_type === 'dmc_package') {
        // For DMC Package, use the dedicated endpoint
        const dmcContent = formData.dmc_content || {};
        const parsed = dmcContent.parsed || {};
        
        // Build route from accommodation options for display
        const route = (parsed.accommodation_options?.[0]?.hotels || []).map(h => ({
          place_name: h.place,
          nights: h.nights
        }));
        
        const dmcPayload = {
          route_type: 'dmc_package',
          dmc_content: dmcContent,
          dmc_calculation: effectiveCalc,
          guest_name: formData.guest_name,
          guest_phone: formData.guest_phone,
          guest_country_code: formData.guest_country_code || '+91',
          guest_email: formData.guest_email,
          arrival_city: formData.arrival_city,
          arrival_point: formData.arrival_point || 'Airport',
          arrival_date: formData.arrival_date,
          departure_city: formData.departure_city,
          tour_start_date: formData.arrival_date,
          total_nights: parsed.total_nights || 0,
          total_days: parsed.total_days || 0,
          no_adults: formData.no_adults,
          no_children_with_bed: formData.no_children_with_bed,
          no_children_no_bed: formData.no_children_no_bed,
          no_infants: formData.no_infants,
          markup_pct: formData.markup_type === 'percent' ? (formData.markup_pct || 0) : 0,
          markup_type: formData.markup_type || 'percent',
          markup_amount: formData.markup_type === 'amount' ? (formData.markup_amount || 0) : 0,
          discount_pct: formData.discount_type === 'percent' || !formData.discount_type ? (formData.discount_pct || 0) : 0,
          discount_type: formData.discount_type || 'percent',
          discount_amount: formData.discount_type === 'amount' ? (formData.discount_amount || 0) : 0,
          gst_percentage: formData.gst_percentage || 0,
          gst_included: formData.gst_included,
          gst_apply_on: formData.gst_apply_on || 'total',
          quotation_format: formData.quotation_format || 'total',
          show_breakup: formData.show_breakup,
          show_discount_separately: formData.show_discount_separately || false,
          inclusions: formData.inclusions,
          exclusions: formData.exclusions,
          payment_terms: formData.payment_terms,
          cancellation_policy: formData.cancellation_policy,
          terms_conditions: formData.terms_conditions,
          room_config: {
            single_rooms: formData.room_config?.single_rooms || 0,
            double_rooms: formData.room_config?.double_rooms || Math.ceil(formData.no_adults / 2),
            triple_rooms: formData.room_config?.triple_rooms || 0,
          },
          // BUG-17 — propagate DMC wizard misc_costs to backend so they flow
          // through `request.misc_costs` → category_pricing aggregation.
          misc_costs: formData.misc_costs || [],
          // Carry payment policy and tour personalisation fields too — they
          // were previously dropped from the DMC payload.
          payment_policy_id: formData.payment_policy_id || null,
        };

        response = await api.post('/quotations', dmcPayload);
      } else {
        // For predefined/custom routes, use the standard endpoint
        // Get extra_adults from room config or calculation result
        const extraAdults = formData.room_config?.adult_extra_beds || effectiveCalc?.extra_adults || 0;
        
        const payload = {
          route_id: formData.route_type === 'predefined' ? formData.route_id : null,
          custom_route: formData.route_type === 'custom' ? formData.custom_route : null,
          guest_name: formData.guest_name,
          guest_phone: formData.guest_phone,
          guest_country_code: formData.guest_country_code || '+91',
          guest_email: formData.guest_email,
          arrival_city: formData.arrival_city,
          arrival_point: formData.arrival_point,
          arrival_date: formData.arrival_date,
          departure_city: formData.departure_city,
          tour_start_date: formData.arrival_date,
          no_adults: formData.no_adults,
          extra_adults: extraAdults,
          no_children_with_bed: formData.no_children_with_bed,
          no_children_no_bed: formData.no_children_no_bed,
          no_infants: formData.no_infants,
          hotel_category: formData.hotel_category,
          selected_categories: formData.selected_categories,
          meal_plan: formData.meal_plans[0],
          meal_plans: formData.meal_plans,
          markup_pct: formData.markup_pct,
          markup_type: toCanonicalMarkupType(formData.markup_type, formData.markup_per_person),
          markup_amount: formData.markup_type === 'amount' ? (formData.markup_amount || 0) : 0,
          markup_per_person: formData.markup_per_person || false,
          discount_pct: formData.discount_type === 'percent' || !formData.discount_type ? (formData.discount_pct || 0) : 0,
          discount_type: formData.discount_type || 'percent',
          discount_amount: formData.discount_type === 'amount' ? (formData.discount_amount || 0) : 0,
          discount_per_person: formData.discount_per_person || false,
          gst_percentage: formData.gst_percentage,
          gst_included: formData.gst_included,
          gst_apply_on: formData.gst_apply_on || 'total',
          quotation_format: formData.quotation_format || 'total',
          itinerary_type: formData.itinerary_type,
          show_breakup: formData.show_breakup,
          show_discount_separately: formData.show_discount_separately || false,
          show_misc_table: formData.show_misc_table !== false,
          misc_costs: formData.misc_costs || [],
          inclusions: formData.inclusions,
          exclusions: formData.exclusions,
          payment_terms: formData.payment_terms,
          cancellation_policy: formData.cancellation_policy,
          terms_conditions: formData.terms_conditions,
          // Room configuration
          single_rooms: formData.room_config?.single_rooms || formData.single_rooms || 0,
          double_rooms: formData.room_config?.double_rooms || formData.double_rooms || 1,
          triple_rooms: formData.room_config?.triple_rooms || formData.triple_rooms || 0,
          // Classification mode for hotel categories
          classification_mode: formData.classification_mode || 'PRICE_RANGE',
          // Hotel overrides for user-selected hotels
          hotel_overrides: formData.hotel_overrides || null,
          // Point-to-Point transfers
          p2p_transfers: formData.p2p_transfers || null,
          // Clone fields (if cloning)
          parent_quote_id: formData.parent_quote_id || null,
          clone_sequence: formData.clone_sequence || 0,
          clone_reference: formData.clone_reference || null,
          version_of: formData.version_of || null,
          // Vehicle selection - important for transfer cost calculation
          vehicle_type_selected: formData.vehicle_type_selected || formData.base_vehicle_type || null,
          // Base vehicle type for uplift calculation
          base_vehicle_type: formData.base_vehicle_type || null,
          // #289720: ticked uplift vehicles, so only those show in the saved quote
          selected_vehicles: selectedVehicles && selectedVehicles.length > 0 ? selectedVehicles : null,
        };

        response = await api.post('/quotations', payload);
      }
      
      toast.success('Quotation created successfully!');
      navigate(buildPath(`/quotations/${response.data.quotation_id}`));
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMessage = 'Failed to create quotation';
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI validation errors: name the offending field, otherwise the
        // operator just sees "Input should be a valid string" with no clue.
        errorMessage = detail
          .map((e) => {
            const field = Array.isArray(e.loc)
              ? e.loc.filter((p) => p !== 'body').join('.')
              : '';
            const msg = e.msg || e.message || JSON.stringify(e);
            return field ? `${field}: ${msg}` : msg;
          })
          .join(', ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6 max-w-5xl mx-auto" data-testid="create-quotation-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            {formData.version_of ? `New Version of ${formData.source_reference}` : 'Create Quotation'}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">
            {formData.version_of
              ? <span data-testid="version-mode-banner">Started from {formData.source_version_label}. Saving creates {formData.next_version_label}; earlier versions stay unchanged.</span>
              : formData.clone_of
                ? <span data-testid="clone-mode-banner">Copy of {formData.source_reference}. Saving creates a new quote number.</span>
                : 'Create a new tour package quotation'}
            {isB2BAgent && user?.company_name && (
              <span className="ml-2 text-blue-600">({user.company_name})</span>
            )}
          </p>
        </div>

        {/* Progress Steps - 3 Tabs */}
        <StepIndicator 
          currentStep={step} 
          onStepClick={goToStep}
          calculationResult={calculationResult}
          totalSteps={3}
          staleWarning={isStale}
        />

        {/* Step 1: Guest & Trip (Merged) */}
        {step === 1 && (
          <GuestTripStep
            formData={formData}
            handleInputChange={handleInputChange}
            routes={routes}
            places={places}
            vehicleOptions={vehicleOptions}
            setFormData={setFormData}
            addCustomStop={addCustomStop}
            removeCustomStop={removeCustomStop}
            updateCustomStop={updateCustomStop}
            totalPax={totalPax}
            suggestedSingleRooms={suggestedSingleRooms}
            suggestedDoubleRooms={suggestedDoubleRooms}
            travelAgents={travelAgents}
            isB2BAgent={isB2BAgent}
            onPlacesRefresh={refreshPlaces}
          />
        )}

        {/* Step 2: Preferences (for predefined/custom routes) */}
        {step === 2 && formData.route_type !== 'custom_package' && (
          <PreferencesStep
            formData={formData}
            handleInputChange={handleInputChange}
            toggleMealPlan={toggleMealPlan}
            toggleCategory={toggleCategory}
            availableCategories={availableCategories}
            availableMealPlans={availableMealPlans}
            calculating={calculating}
            calculationResult={calculationResult}
            loading={loading}
            onCalculate={calculateQuotationDebounced}
            classificationMode={classificationMode}
            switchClassificationMode={switchClassificationMode}
            onSave={() => saveQuotation()}
            vehicleOptions={vehicleOptions}
            selectedVehicles={selectedVehicles}
            onVehicleSelectionChange={handleVehicleSelectionChange}
            routes={routes}
          />
        )}

        {/* Custom Package Step 2 */}
        {step === 2 && formData.route_type === 'custom_package' && (
          <div className="space-y-4">
            {/* Trip Summary */}
            <TripSummaryAccordion
              formData={formData}
              calculationResult={calculationResult}
              customPackageData={formData.custom_package_data}
              defaultOpen={false}
            />

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-navy" />
                  Custom Package - Hotel Selection
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">Select hotels for each stop in your custom package</p>
              </CardHeader>
              <CardContent>
                <CustomPackageHotelSelector
                  customPackageData={formData.custom_package_data}
                  hotelSelections={customPackageHotelSelections}
                  onSelectionsChange={setCustomPackageHotelSelections}
                  formData={formData}
                  handleInputChange={handleInputChange}
                  vehicleOptions={vehicleOptions}
                  places={places}
                />
                
                {/* Calculate Button */}
                <div className="mt-6">
                  <Button
                    onClick={calculateQuotationDebounced}
                    className="w-full bg-navy hover:bg-navy/90"
                    disabled={calculating}
                    data-testid="calculate-custom-package-btn"
                  >
                    {calculating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Calculate Custom Package
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Step 3: Quote Preview & Save */}
        {step === 3 && (
          <PreferencesStep
            formData={formData}
            handleInputChange={handleInputChange}
            toggleMealPlan={toggleMealPlan}
            toggleCategory={toggleCategory}
            availableCategories={availableCategories}
            availableMealPlans={availableMealPlans}
            calculating={calculating}
            calculationResult={calculationResult}
            loading={loading}
            onCalculate={calculateQuotationDebounced}
            classificationMode={classificationMode}
            switchClassificationMode={switchClassificationMode}
            onSave={() => saveQuotation()}
            vehicleOptions={vehicleOptions}
            selectedVehicles={selectedVehicles}
            onVehicleSelectionChange={handleVehicleSelectionChange}
            routes={routes}
            showOnlyPreview={true}
            hasChanges={isStale}
            staleChanges={staleChanges}
            onRecalculateAndSave={() => saveQuotation({ recalculate: true })}
            setHasChanges={setHasChanges}
            onBack={() => setStep(2)}
          />
        )}

        {/* Navigation Buttons - Only show on Step 1 */}
        {step === 1 && (
          <NavigationButtons
            step={step}
            totalSteps={3}
            onPrev={prevStep}
            onNext={nextStep}
            calculating={calculating}
            onCancel={() => navigate(buildPath('/dashboard'))}
          />
        )}
      </div>
    </Layout>
  );
};

export default CreateQuotation;
